import apiClient from "@services/api/apiClient.jsx";

const branchApi = {
    /**
     * 지점 목록 조회
     * @param {Object} params - 검색 파라미터
     * @param {number} params.page - 페이지 번호 (0부터 시작)
     * @param {number} params.size - 페이지 크기
     * @param {string} params.sort - 정렬 기준
     * @param {string} params.name - 지점명
     * @param {string} params.country - 국가
     * @param {string} params.city - 도시
     * @param {string} params.status - 상태
     * @returns {Promise} API 응답
     */
    getBranches: async (params = {}) => {
        try {
            const response = await apiClient.get("/infra/branches", { params });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch branches:", error);
            throw error;
        }
    },

    /**
     * 지점 상세 조회
     * @param {number} id - 지점 ID
     * @returns {Promise} API 응답
     */
    getBranchById: async (id) => {
        try {
            const response = await apiClient.get(`/infra/branches/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch branch ${id}:`, error);
            throw error;
        }
    },

    /**
     * 지점 생성
     * @param {Object} branchData - 생성할 지점 데이터
     * @returns {Promise} API 응답
     */
    createBranch: async (branchData) => {
        try {
            // LocalDateTime 형식으로 변환
            const requestData = {
                ...branchData,
                openDate: branchData.openDate ? `${branchData.openDate}T00:00:00` : null,
                closedDate: branchData.closedDate ? `${branchData.closedDate}T00:00:00` : null
            };

            const response = await apiClient.post("/infra/branches", requestData);
            return response.data;
        } catch (error) {
            console.error("Failed to create branch:", error);
            throw error;
        }
    },

    /**
     * 지점 정보 수정
     * @param {number} id - 지점 ID
     * @param {Object} branchData - 수정할 지점 데이터
     * @returns {Promise} API 응답
     */
    updateBranch: async (id, branchData) => {
        try {
            // LocalDateTime 형식으로 변환
            const requestData = {
                ...branchData,
                openDate: branchData.openDate ? `${branchData.openDate}T00:00:00` : null,
                closedDate: branchData.closedDate ? `${branchData.closedDate}T00:00:00` : null
            };

            const response = await apiClient.put(`/infra/branches/${id}`, requestData);
            return response.data;
        } catch (error) {
            console.error(`Failed to update branch ${id}:`, error);
            throw error;
        }
    },

    /**
     * 지점 삭제 (Soft delete)
     * @param {number} id - 지점 ID
     * @returns {Promise} API 응답
     */
    deleteBranch: async (id) => {
        try {
            const response = await apiClient.delete(`/infra/branches/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Failed to delete branch ${id}:`, error);
            throw error;
        }
    },

    /**
     * 다중 지점 삭제
     * @param {Array<number>} ids - 삭제할 지점 ID 배열
     * @returns {Promise} API 응답
     */
    deleteBranches: async (ids) => {
        try {
            const deletePromises = ids.map(id => branchApi.deleteBranch(id));
            const results = await Promise.allSettled(deletePromises);

            const succeeded = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.filter(r => r.status === 'rejected').length;

            return {
                success: failed === 0,
                succeeded,
                failed,
                total: ids.length
            };
        } catch (error) {
            console.error("Failed to delete multiple branches:", error);
            throw error;
        }
    },

    /**
     * 국가별 지점 목록 조회
     * @param {string} country - 국가명
     * @param {Object} params - 페이징 파라미터
     * @returns {Promise} API 응답
     */
    getBranchesByCountry: async (country, params = {}) => {
        try {
            const response = await apiClient.get(`/infra/branches/country/${country}`, { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch branches for country ${country}:`, error);
            throw error;
        }
    },

    /**
     * 상태별 지점 목록 조회
     * @param {string} status - 상태 (ACTIVE, INACTIVE)
     * @param {Object} params - 페이징 파라미터
     * @returns {Promise} API 응답
     */
    getBranchesByStatus: async (status, params = {}) => {
        try {
            const response = await apiClient.get(`/infra/branches/status/${status}`, { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch branches with status ${status}:`, error);
            throw error;
        }
    }
};

export default branchApi;